import React, { useState, useCallback, useRef } from 'react';

import { Grid, Stack } from '@mui/material';
import { initialValue } from 'organisms/Logs/LogsTable';

import axios, { AxiosResponse } from 'axios';
import debounce from 'lodash-es/debounce';
import dayjs from 'dayjs';

import { FilterAutocomplete } from 'atoms/TableFilter';
import { StyledFilterWrapper } from 'molecules/Filter/Filter.styles';
import Filter from 'organisms/Filter';

import { IFilterOption } from 'organisms/Filter/types';
import ActivityLogTable from './ActivityLogsTable';
import { transformString } from 'organisms/Filter/helpers';
import { Order } from 'organisms/InvoicesTable/types';
import { snakeCase } from 'lodash-es';

interface IParams {
  email?: string;
  page: number;
  limit: number;
  date_from?: string;
  date_to?: string;
  sort?: string;
  sort_order?: Order;
}
type FilterOptions = {
  actions: string[];
  modules: string[];
  performed_by: { id: string; name: string }[];
};
export type filterApplyType = {
  date_from: string;
  date_to: string;
  action: string;
  target_type: string;
  user_id: string;
};

export type sortByType = 'created_at' | 'user_name' | 'action' | 'module';

const fetchList = async (name: string) => {
  const response: AxiosResponse<FilterOptions> = await axios.get(
    `${process.env.REACT_APP_USER_SERVICE}/activity/filters`
  );

  let key: 'actions' | 'modules' | 'performed_by' | '' = '';
  switch (name) {
    case 'action':
      key = 'actions';
      break;
    case 'user_id':
      key = 'performed_by';
      break;
    case 'target_type':
      key = 'modules';
      break;
    default:
      break;
  }

  if (!key) return [];

  if (key === 'actions' || key === 'modules') {
    return response.data?.[key].map((item: string) => ({
      id: item,
      name: transformString(item),
    }));
  }

  return (response.data?.[key] || []) as { id: string; name: string }[];
};

const initialOptions: Array<IFilterOption> = [
  { code: 'created_at', title: 'Created Date', active: true },
  { code: 'action', title: 'Action', fetchList },
  { code: 'target_type', title: 'Module', fetchList },
  { code: 'user_id', title: 'Performed By', fetchList },
];

const ActivityLogsBlock = () => {
  const [multiFilter, setMultiFilter] = useState<Partial<filterApplyType>>({});
  const [name, setName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<sortByType>('created_at');

  const searchRef = useRef<HTMLInputElement | null>(null);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: sortByType
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterChange = useCallback(
    (nextFilterValue: { [key: string]: string[] }) => {
      const filterApply = Object?.entries(nextFilterValue).reduce(
        (acc, [key, value]) => {
          if (key === 'created_at' && value && value[0] && value[1]) {
            acc.date_from = dayjs(dayjs(value[0])).format('YYYY-MM-DD');
            acc.date_to = dayjs(dayjs(value[1])).format('YYYY-MM-DD');
          }

          if (key === 'action' && value[0]) {
            acc.action = value?.join(',');
          }

          if (key === 'target_type' && value[0]) {
            acc.target_type = value?.join(',');
          }

          if (key === 'user_id' && value[0]) {
            acc.user_id = value?.join(',');
          }

          return acc;
        },
        {} as filterApplyType
      );

      if (JSON.stringify(filterApply) === JSON.stringify(multiFilter)) return;

      setMultiFilter(filterApply);
    },
    [name, multiFilter]
  );

  const debouncedChangeHandler = useCallback(
    debounce((event) => {
      setName(event.target.value);
    }, 300),
    []
  );

  const onReset = useCallback(() => {
    if (searchRef.current) {
      searchRef.current.value = '';
    }
    setName('');
  }, []);

  const fetchPageData = useCallback(
    async (filter: typeof initialValue) => {
      try {
        let sort = '';
        setLoading(true);

        const { page, limit } = filter;

        const params: IParams = { ...prepareParams(), page, limit };

        if (orderBy) {
          sort = `sort[${snakeCase(orderBy)}]=${order}`;
        }

        const { data } = await axios.get(
          `${process.env.REACT_APP_USER_SERVICE}/activity/logs?${sort || ''}`,
          { params }
        );
        const { items: logs, count } = data;

        setLoading(false);

        return { logs, count };
      } catch (e) {
        // eslint-disable-next-line
        console.error(e);
        setLoading(false);
        return { logs: [], count: 0 };
      }
    },
    [name, multiFilter, orderBy, order]
  );

  function prepareParams() {
    return {
      ...(name?.trim() ? { search: name } : {}),
      ...(multiFilter || {}),
    };
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <StyledFilterWrapper
          sx={{
            '.filter-popover-container': {
              top: '215px',
              left: '30px',
            },
          }}
        >
          <Stack direction="row" gap={3}>
            <Filter
              loading={loading}
              options={initialOptions}
              onChange={handleFilterChange}
            />

            <FilterAutocomplete
              isFreeSolo={!!name}
              value={name}
              options={[]}
              placeholder="Search by user name, entity name"
              tooltip="Search by user name, entity name"
              onChange={debouncedChangeHandler}
              onReset={onReset}
            />
          </Stack>
        </StyledFilterWrapper>

        <ActivityLogTable
          loading={loading}
          fetch={fetchPageData}
          name={name}
          filter={multiFilter}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
        />
      </Grid>
    </Grid>
  );
};

export default ActivityLogsBlock;
